import { create } from "zustand";
import { devtools } from "zustand/middleware";

interface TeamManagementUIState {
  // View state
  activeTab: "active" | "inactive";
  selectedMemberIds: string[];

  // Dialog states
  isCreateDialogOpen: boolean;
  isEditDialogOpen: boolean;
  isDeleteDialogOpen: boolean;
  isEmailChangeDialogOpen: boolean;
  isLinkUserDialogOpen: boolean;
  isSetupAccountDialogOpen: boolean;
  isServiceRestrictionDialogOpen: boolean;
  editingMemberId: string | null;
  deletingMemberId: string | null;
  emailChangeMemberId: string | null;
  linkUserMemberId: string | null;
  setupAccountMemberId: string | null;
  serviceRestrictionMemberId: string | null;

  // Actions
  setActiveTab: (tab: "active" | "inactive") => void;
  setSelectedMemberIds: (ids: string[]) => void;
  toggleMemberSelection: (id: string) => void;
  clearSelection: () => void;

  // Dialog actions
  openCreateDialog: () => void;
  closeCreateDialog: () => void;
  openEditDialog: (memberId: string) => void;
  closeEditDialog: () => void;
  openDeleteDialog: (memberId: string) => void;
  closeDeleteDialog: () => void;
  openEmailChangeDialog: (memberId: string) => void;
  closeEmailChangeDialog: () => void;
  openLinkUserDialog: (memberId: string) => void;
  closeLinkUserDialog: () => void;
  openSetupAccountDialog: (memberId: string) => void;
  closeSetupAccountDialog: () => void;
  openServiceRestrictionDialog: (memberId: string) => void;
  closeServiceRestrictionDialog: () => void;
  closeAllDialogs: () => void;
}

export const useTeamManagementUIStore = create<TeamManagementUIState>()(
  devtools(
    (set, get) => ({
      // Initial state
      activeTab: "active",
      selectedMemberIds: [],
      isCreateDialogOpen: false,
      isEditDialogOpen: false,
      isDeleteDialogOpen: false,
      isEmailChangeDialogOpen: false,
      isLinkUserDialogOpen: false,
      isSetupAccountDialogOpen: false,
      isServiceRestrictionDialogOpen: false,
      editingMemberId: null,
      deletingMemberId: null,
      emailChangeMemberId: null,
      linkUserMemberId: null,
      setupAccountMemberId: null,
      serviceRestrictionMemberId: null,

      // Actions
      setActiveTab: (tab) => set({ activeTab: tab }),

      setSelectedMemberIds: (ids) => set({ selectedMemberIds: ids }),

      toggleMemberSelection: (id) => {
        const { selectedMemberIds } = get();
        const newIds = selectedMemberIds.includes(id)
          ? selectedMemberIds.filter((selectedId) => selectedId !== id)
          : [...selectedMemberIds, id];
        set({ selectedMemberIds: newIds });
      },

      clearSelection: () => set({ selectedMemberIds: [] }),

      // Dialog actions
      openCreateDialog: () =>
        set({
          isCreateDialogOpen: true,
          isEditDialogOpen: false,
          isDeleteDialogOpen: false,
          isEmailChangeDialogOpen: false,
          isLinkUserDialogOpen: false,
        }),

      closeCreateDialog: () => set({ isCreateDialogOpen: false }),

      openEditDialog: (memberId) =>
        set({
          isEditDialogOpen: true,
          editingMemberId: memberId,
          isCreateDialogOpen: false,
          isDeleteDialogOpen: false,
          isEmailChangeDialogOpen: false,
          isLinkUserDialogOpen: false,
        }),

      closeEditDialog: () =>
        set({
          isEditDialogOpen: false,
          editingMemberId: null,
        }),

      openDeleteDialog: (memberId) =>
        set({
          isDeleteDialogOpen: true,
          deletingMemberId: memberId,
          isCreateDialogOpen: false,
          isEditDialogOpen: false,
          isEmailChangeDialogOpen: false,
          isLinkUserDialogOpen: false,
        }),

      closeDeleteDialog: () =>
        set({
          isDeleteDialogOpen: false,
          deletingMemberId: null,
        }),

      openEmailChangeDialog: (memberId) =>
        set({
          isEmailChangeDialogOpen: true,
          emailChangeMemberId: memberId,
          isCreateDialogOpen: false,
          isEditDialogOpen: false,
          isDeleteDialogOpen: false,
          isLinkUserDialogOpen: false,
        }),

      closeEmailChangeDialog: () =>
        set({
          isEmailChangeDialogOpen: false,
          emailChangeMemberId: null,
        }),

      openLinkUserDialog: (memberId) =>
        set({
          isLinkUserDialogOpen: true,
          linkUserMemberId: memberId,
          isCreateDialogOpen: false,
          isEditDialogOpen: false,
          isDeleteDialogOpen: false,
          isEmailChangeDialogOpen: false,
        }),

      closeLinkUserDialog: () =>
        set({
          isLinkUserDialogOpen: false,
          linkUserMemberId: null,
        }),

      openSetupAccountDialog: (memberId) =>
        set({
          isSetupAccountDialogOpen: true,
          setupAccountMemberId: memberId,
          isCreateDialogOpen: false,
          isEditDialogOpen: false,
          isDeleteDialogOpen: false,
          isEmailChangeDialogOpen: false,
          isLinkUserDialogOpen: false,
        }),

      closeSetupAccountDialog: () =>
        set({
          isSetupAccountDialogOpen: false,
          setupAccountMemberId: null,
        }),

      openServiceRestrictionDialog: (memberId) =>
        set({
          isServiceRestrictionDialogOpen: true,
          serviceRestrictionMemberId: memberId,
          isCreateDialogOpen: false,
          isEditDialogOpen: false,
          isDeleteDialogOpen: false,
          isEmailChangeDialogOpen: false,
          isLinkUserDialogOpen: false,
          isSetupAccountDialogOpen: false,
        }),

      closeServiceRestrictionDialog: () =>
        set({
          isServiceRestrictionDialogOpen: false,
          serviceRestrictionMemberId: null,
        }),

      closeAllDialogs: () =>
        set({
          isCreateDialogOpen: false,
          isEditDialogOpen: false,
          isDeleteDialogOpen: false,
          isEmailChangeDialogOpen: false,
          isLinkUserDialogOpen: false,
          isSetupAccountDialogOpen: false,
          isServiceRestrictionDialogOpen: false,
          editingMemberId: null,
          deletingMemberId: null,
          emailChangeMemberId: null,
          linkUserMemberId: null,
          setupAccountMemberId: null,
          serviceRestrictionMemberId: null,
        }),
    }),
    { name: "team-management-ui-store" }
  )
);
